import { useEffect, useState } from 'react';

type UseObservedElementSizeOptions = {
  selector?: string;
  enabled?: boolean;
  minWidth?: number;
  minHeight?: number;
};

type ObservedElementSize = {
  width: number;
  height: number;
};

function useObservedElementSize<T extends HTMLElement = HTMLDivElement>({
  selector,
  enabled = true,
  minWidth = 0,
  minHeight = 0
}: UseObservedElementSizeOptions = {}): [
  (node: T | null) => void,
  ObservedElementSize
] {
  const [container, setContainer] = useState<T | null>(null);
  const [size, setSize] = useState<ObservedElementSize>({
    width: minWidth,
    height: minHeight
  });

  useEffect(() => {
    if (!enabled || !container) return;

    const observedElement = selector
      ? container.querySelector<HTMLElement>(selector)
      : container;

    if (!observedElement) return;

    const updateSize = () => {
      const rect = observedElement.getBoundingClientRect();
      setSize({
        width: Math.max(minWidth, Math.ceil(rect.width)),
        height: Math.max(minHeight, Math.ceil(rect.height))
      });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(observedElement);

    return () => observer.disconnect();
  }, [container, enabled, selector, minWidth, minHeight]);

  return [setContainer, size];
}

export default useObservedElementSize;
